import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import Navbar from '../../components/navbar/navbar'
import apinode from '../../services/apinode'
import ZPLRenderer from './ZPLRender'

import {
  AlertCircle,
  Barcode,
  CalendarClock,
  Camera,
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Copy,
  Download,
  Eye,
  Filter,
  Image as ImageIcon,
  Mail,
  MapPin,
  MoreVertical,
  Package,
  PackageOpen,
  PackageSearch,
  Phone,
  Printer,
  Receipt,
  RefreshCw,
  Search,
  SortAsc,
  SortDesc,
  Store,
  Tag,
  TrendingUp,
  Truck,
  User,
  X,
} from 'lucide-react'

const OrderDetails = () => {
  const { idpedido, id_conta } = useParams()
  const [isTailwindReady, setIsTailwindReady] = useState(false)
  const [orderData, setOrderData] = useState({})
  const [showCarousel, setShowCarousel] = useState(false)
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [zpl, setZpl] = useState([])
  const [fotos, setFotos] = useState([])
  const [isLoadingOrderData, setIsLoadingOrderData] = useState(false)
  const [isLoadingFotos, setIsLoadingFotos] = useState(false)
  const [showModalZpl, setShowModalZpl] = useState(false)
  const [openKits, setOpenKits] = useState({})
  const [error, setError] = useState(null)
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  // UX state
  const [search, setSearch] = useState('')
  const [minQty, setMinQty] = useState('')
  const [sortBy, setSortBy] = useState('produto') // produto | sku | valor | qtd
  const [sortDir, setSortDir] = useState('asc') // asc | desc
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [page, setPage] = useState(1)

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    msg: '',
    variant: 'success',
  })

  const searchRef = useRef(null)

  // Load Tailwind CSS dinamicamente
  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://cdn.tailwindcss.com'
    script.async = true
    script.onload = () => setIsTailwindReady(true)
    document.head.appendChild(script)

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
  }, [])

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape' && showCarousel) {
        setShowCarousel(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showCarousel])

  const fetchFotos = useCallback(async () => {
    setIsLoadingFotos(true)
    try {
      const fotosResponse = await apinode.get(
        `/pedido/foto/${idpedido}/${id_conta}`,
        {
          auth: { username: 'admin', password: 'admin' },
        },
      )
      const primary = Array.isArray(fotosResponse.data)
        ? fotosResponse.data
        : []
      if (primary.length > 0) {
        setFotos(primary)
      } else {
        const fallback = await apinode.get(
          `/foto/fotos/${idpedido}/${id_conta}`,
        )
        setFotos(Array.isArray(fallback.data) ? fallback.data : [])
      }
    } catch (err) {
      console.error('Erro ao buscar as fotos do pedido:', err)
      setError('Falha ao carregar fotos.')
    } finally {
      setIsLoadingFotos(false)
    }
  }, [idpedido, id_conta])

  const fetchOrderData = useCallback(async () => {
    setIsLoadingOrderData(true)
    setError(null)
    try {
      const orderDetailsResponse = await apinode.get(
        `/orderdetails/${idpedido}/${id_conta}`,
      )
      setOrderData(orderDetailsResponse.data)

      // Fotos e ZPL
      fetchFotos()
      const zplResponse = await apinode.get(
        `/pedido/foto/${idpedido}/${id_conta}`,
        {
          auth: { username: 'admin', password: 'admin' },
        },
      )
      setZpl(zplResponse.data)
    } catch (err) {
      console.error('Erro ao buscar os dados do pedido:', err)
      setError('Não foi possível carregar os dados do pedido.')
    } finally {
      setIsLoadingOrderData(false)
    }
  }, [idpedido, id_conta, fetchFotos])

  useEffect(() => {
    fetchOrderData()
  }, [fetchOrderData])

  // Utils
  const currencyBRL = (n) =>
    typeof n === 'number'
      ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : 'R$ 0,00'

  const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('pt-BR') : '—')
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '—')
  const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('pt-BR') : '—')

  const getStatusMeta = (status) => {
    const map = {
      'Aguardando Pagamento': {
        color: 'text-amber-700',
        bg: 'bg-amber-50 border-amber-200',
        step: 0,
        icon: AlertCircle,
      },
      'Em Processamento': {
        color: 'text-blue-700',
        bg: 'bg-blue-50 border-blue-200',
        step: 1,
        icon: Clock,
      },
      Enviado: {
        color: 'text-emerald-700',
        bg: 'bg-emerald-50 border-emerald-200',
        step: 2,
        icon: Truck,
      },
      Entregue: {
        color: 'text-green-700',
        bg: 'bg-green-50 border-green-200',
        step: 3,
        icon: ClipboardCheck,
      },
      Atendido: {
        color: 'text-green-700',
        bg: 'bg-green-50 border-green-200',
        step: 3,
        icon: ClipboardCheck,
      },
    }
    return (
      map[status] || {
        color: 'text-gray-600',
        bg: 'bg-gray-50 border-gray-200',
        step: 0,
        icon: AlertCircle,
      }
    )
  }

  const showToast = (msg, variant = 'success') => {
    setToast({ show: true, msg, variant })
    setTimeout(
      () => setToast({ show: false, msg: '', variant: 'success' }),
      3000,
    )
  }

  const copy = async (text, label = 'Copiado!') => {
    try {
      await navigator.clipboard.writeText(String(text))
      showToast(label)
    } catch {
      showToast('Falha ao copiar.', 'danger')
    }
  }

  const toggleKitVisibility = (itemId) => {
    setOpenKits((prev) => ({ ...prev, [itemId]: !prev[itemId] }))
  }

  const handleCarouselOpen = (index) => {
    setCarouselIndex(index)
    setShowCarousel(true)
  }

  const handleCarouselClose = () => setShowCarousel(false)

  const {
    pedido_int = [],
    itempedido = [],
    desagrupado_int = [],
    conferidos = [],
  } = orderData
  const pedido = pedido_int[0] || {}

  const totals = useMemo(() => {
    const totalQtd = itempedido.reduce(
      (acc, it) => acc + (Number(it.QUANTIDADE_NOVA) || 0),
      0,
    )
    const totalVlr = itempedido.reduce(
      (acc, it) => acc + (Number(it.VALOR_NOVO) || 0),
      0,
    )
    return { totalQtd, totalVlr }
  }, [itempedido])

  const kitItemsByRef = useMemo(() => {
    const map = {}
    for (const k of desagrupado_int) {
      const ref = k.REFERENCIA_ID
      if (!map[ref]) map[ref] = []
      map[ref].push(k)
    }
    return map
  }, [desagrupado_int])

  const filteredSorted = useMemo(() => {
    const term = search.trim().toLowerCase()
    let rows = itempedido.filter((it) => {
      const okTerm =
        !term ||
        String(it.CODIGO).toLowerCase().includes(term) ||
        String(it.DESCRICAO ?? '')
          .toLowerCase()
          .includes(term)
      const okQty = !minQty || Number(it.QUANTIDADE_NOVA || 0) >= Number(minQty)
      return okTerm && okQty
    })

    const dir = sortDir === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      const by = {
        sku: () =>
          String(a.CODIGO).localeCompare(String(b.CODIGO), 'pt-BR', {
            numeric: true,
          }),
        produto: () =>
          String(a.DESCRICAO || '').localeCompare(
            String(b.DESCRICAO || ''),
            'pt-BR',
            { numeric: true },
          ),
        valor: () => (Number(a.VALOR_NOVO) || 0) - (Number(b.VALOR_NOVO) || 0),
        qtd: () =>
          (Number(a.QUANTIDADE_NOVA) || 0) - (Number(b.QUANTIDADE_NOVA) || 0),
      }[sortBy]()
      return by * dir
    })

    return rows
  }, [itempedido, search, minQty, sortBy, sortDir])

  const pageCount = Math.max(
    1,
    Math.ceil(filteredSorted.length / Number(rowsPerPage || 10)),
  )
  const pageSafe = Math.min(page, pageCount)
  const pageRows = useMemo(() => {
    const start = (pageSafe - 1) * rowsPerPage
    return filteredSorted.slice(start, start + Number(rowsPerPage))
  }, [filteredSorted, pageSafe, rowsPerPage])

  // Reset página quando filtros mudarem
  useEffect(() => {
    setPage(1)
  }, [search, minQty, sortBy, sortDir, rowsPerPage])

  // StatusPill Component
  const StatusPill = ({ status }) => {
    const meta = getStatusMeta(status)
    const Icon = meta.icon
    return (
      <div
        className={`inline-flex items-center px-3 py-2 rounded-full border text-sm font-medium ${meta.color} ${meta.bg}`}
      >
        <Icon
          size={16}
          className="mr-2"
        />
        {status || 'Status não disponível'}
      </div>
    )
  }

  // StatusTimeline Component
  const StatusTimeline = ({ status }) => {
    const steps = [
      { label: 'Aguardando Pagamento', icon: AlertCircle },
      { label: 'Em Processamento', icon: Clock },
      { label: 'Enviado', icon: Truck },
      { label: 'Entregue', icon: ClipboardCheck },
    ]
    const current = getStatusMeta(status).step

    return (
      <div className="flex items-center justify-between w-full max-w-4xl mx-auto">
        {steps.map((step, idx) => {
          const active = idx <= current
          const Icon = step.icon
          return (
            <div
              key={step.label}
              className="flex items-center"
            >
              <div
                className={`flex flex-col items-center ${active ? 'text-blue-600' : 'text-gray-400'}`}
              >
                <div
                  className={`p-3 rounded-full border-2 ${active ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}
                >
                  <Icon size={20} />
                </div>
                <span className="text-xs font-medium mt-2 text-center max-w-20">
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={`h-0.5 w-16 mx-4 ${idx < current ? 'bg-blue-600' : 'bg-gray-200'}`}
                />
              )}
            </div>
          )
        })}
      </div>
    )
  }

  if (!isTailwindReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center space-x-3">
                    <h1 className="text-2xl font-bold text-gray-900">
                      Pedido #{pedido?.PEDIDO || '...'}
                    </h1>
                    <button
                      onClick={() =>
                        copy(
                          pedido?.PEDIDO || '...',
                          'Número do pedido copiado!',
                        )
                      }
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    <CalendarClock
                      size={16}
                      className="mr-1"
                    />
                    {pedido?.DATA_CONFERENCIA
                      ? fmtDateTime(pedido.DATA_CONFERENCIA)
                      : 'Sem data de conferência'}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <StatusPill status={pedido?.STATUS || 'Em Processamento'} />
                <button
                  onClick={() => setShowModalZpl(true)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <Printer
                    size={18}
                    className="mr-2"
                  />
                  Etiqueta
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
            <StatusTimeline status={pedido?.STATUS || 'Em Processamento'} />
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-50 rounded-xl">
                  <Store
                    size={20}
                    className="text-blue-600"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Canal</p>
                  <p className="text-lg font-bold text-gray-900">
                    {pedido?.ID_LOJA || '...'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-50 rounded-xl">
                  <Receipt
                    size={20}
                    className="text-green-600"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">OrderID</p>
                  <p className="text-lg font-bold text-gray-900">
                    {(pedido?.IDPEDIDOLOJA || '-').substring(0, 8)}...
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-50 rounded-xl">
                  <Barcode
                    size={20}
                    className="text-purple-600"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Ship ID</p>
                  <p className="text-lg font-bold text-gray-900">
                    {pedido?.SHIP || '-'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-amber-50 rounded-xl">
                  <PackageSearch
                    size={20}
                    className="text-amber-600"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Itens</p>
                  <p className="text-lg font-bold text-gray-900">
                    {totals.totalQtd.toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-emerald-50 rounded-xl">
                  <TrendingUp
                    size={20}
                    className="text-emerald-600"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Valor Total
                  </p>
                  <p className="text-lg font-bold text-gray-900">
                    {currencyBRL(totals.totalVlr)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Order Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Search and Filters */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        ref={searchRef}
                        type="text"
                        placeholder="Buscar por SKU ou produto..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <div className="relative">
                      <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="number"
                        min="0"
                        placeholder="Qtd mín."
                        value={minQty}
                        onChange={(e) => setMinQty(e.target.value)}
                        className="pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-32"
                      />
                    </div>
                    <select
                      value={`${sortBy}-${sortDir}`}
                      onChange={(e) => {
                        const [by, dir] = e.target.value.split('-')
                        setSortBy(by)
                        setSortDir(dir)
                      }}
                      className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="produto-asc">Produto A-Z</option>
                      <option value="produto-desc">Produto Z-A</option>
                      <option value="sku-asc">SKU A-Z</option>
                      <option value="sku-desc">SKU Z-A</option>
                      <option value="valor-asc">Menor Valor</option>
                      <option value="valor-desc">Maior Valor</option>
                      <option value="qtd-asc">Menor Qtd</option>
                      <option value="qtd-desc">Maior Qtd</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <PackageSearch className="w-5 h-5 mr-2 text-blue-600" />
                      Itens do Pedido
                    </h3>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => showToast('CSV exportado com sucesso!')}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <Download
                          size={16}
                          className="mr-2"
                        />
                        CSV
                      </button>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SKU
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Produto
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vlr. Uni.
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Qtd
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pageRows?.length > 0 ? (
                        pageRows.map((item) => {
                          const kits = kitItemsByRef[item.ID] || []
                          const isOpen = !!openKits[item.ID]
                          return (
                            <React.Fragment key={item.ID}>
                              <tr className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                  <span className="font-mono text-sm font-medium text-gray-900">
                                    {item.CODIGO}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium text-gray-900">
                                      {(item.DESCRICAO || '').toUpperCase()}
                                    </span>
                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                      ANÚNCIO
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-right font-medium text-gray-900">
                                  {currencyBRL(Number(item.VALORUNITARIO))}
                                </td>
                                <td className="px-6 py-4 text-right font-medium text-gray-900">
                                  {Number(
                                    item.QUANTIDADE_NOVA || 0,
                                  ).toLocaleString('pt-BR')}
                                </td>
                                <td className="px-6 py-4 text-right font-bold text-gray-900">
                                  {currencyBRL(Number(item.VALOR_NOVO))}
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <div className="flex items-center justify-center space-x-2">
                                    {kits.length > 0 && (
                                      <button
                                        onClick={() =>
                                          toggleKitVisibility(item.ID)
                                        }
                                        className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                      >
                                        {isOpen ? (
                                          <ChevronDown size={16} />
                                        ) : (
                                          <ChevronRight size={16} />
                                        )}
                                      </button>
                                    )}
                                    <button
                                      onClick={() =>
                                        copy(item.CODIGO, 'SKU copiado!')
                                      }
                                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                                      title="Copiar SKU"
                                    >
                                      <Copy size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>

                              {/* Kit Items */}
                              {isOpen &&
                                kits.map((kit) => (
                                  <tr
                                    key={kit.ID}
                                    className="bg-blue-50"
                                  >
                                    <td className="px-6 py-3 pl-12">
                                      <span className="font-mono text-sm text-gray-600">
                                        {kit.CODIGO}
                                      </span>
                                    </td>
                                    <td className="px-6 py-3">
                                      <div className="flex items-center space-x-2">
                                        <span className="text-sm text-blue-700">
                                          {(kit.DESCRICAO || '').toUpperCase()}
                                        </span>
                                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                                          COMPOSIÇÃO
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-6 py-3 text-right text-sm text-gray-600">
                                      {currencyBRL(Number(kit.VALORUNITARIO))}
                                    </td>
                                    <td className="px-6 py-3 text-right text-sm text-gray-600">
                                      {Number(
                                        kit.QUANTIDADE || 0,
                                      ).toLocaleString('pt-BR')}
                                    </td>
                                    <td className="px-6 py-3 text-right text-sm font-medium text-gray-700">
                                      {currencyBRL(Number(kit.VALOR_NOVO))}
                                    </td>
                                    <td className="px-6 py-3"></td>
                                  </tr>
                                ))}
                            </React.Fragment>
                          )
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan="6"
                            className="px-6 py-12 text-center"
                          >
                            <div className="flex flex-col items-center">
                              <PackageSearch className="w-12 h-12 text-gray-300 mb-4" />
                              <p className="text-gray-500">
                                Nenhum item encontrado
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {filteredSorted?.length > 0 && (
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td
                            colSpan="3"
                            className="px-6 py-4"
                          ></td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-sm text-gray-600 mr-2">
                              Total itens:
                            </span>
                            <span className="font-bold text-gray-900">
                              {totals.totalQtd.toLocaleString('pt-BR')}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <span className="text-sm text-gray-600 mr-2">
                              Total:
                            </span>
                            <span className="font-bold text-gray-900 text-lg">
                              {currencyBRL(totals.totalVlr)}
                            </span>
                          </td>
                          <td className="px-6 py-4"></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <span>
                      Página {pageSafe} de {pageCount} — {filteredSorted.length}{' '}
                      itens
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={rowsPerPage}
                      onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {[10, 20, 50, 100].map((n) => (
                        <option
                          key={n}
                          value={n}
                        >
                          {n}/página
                        </option>
                      ))}
                    </select>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => setPage(1)}
                        disabled={pageSafe <= 1}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100"
                      >
                        <ChevronFirst size={16} />
                      </button>
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={pageSafe <= 1}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        onClick={() =>
                          setPage((p) => Math.min(pageCount, p + 1))
                        }
                        disabled={pageSafe >= pageCount}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100"
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        onClick={() => setPage(pageCount)}
                        disabled={pageSafe >= pageCount}
                        className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100"
                      >
                        <ChevronLast size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Checked Items */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <ClipboardCheck className="w-5 h-5 mr-2 text-green-600" />
                    Itens Conferidos
                  </h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SKU
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Produto
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Qtd
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Hora
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {conferidos?.length > 0 ? (
                        conferidos.map((item) => (
                          <tr
                            key={item.ID}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4">
                              <span className="font-mono text-sm font-medium text-gray-900">
                                {item.SKU}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-medium text-gray-900">
                                {(item.DESCRICAO || '').toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right font-medium text-gray-900">
                              {Number(item.QTDE || 0).toLocaleString('pt-BR')}
                            </td>
                            <td className="px-6 py-4 text-center text-sm text-gray-600">
                              {fmtTime(item.HORA_CONF)}
                            </td>
                            <td className="px-6 py-4 text-center text-sm text-gray-600">
                              {fmtDate(item.DATA_CONF)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="5"
                            className="px-6 py-12 text-center"
                          >
                            <div className="flex flex-col items-center">
                              <ClipboardCheck className="w-12 h-12 text-gray-300 mb-4" />
                              <p className="text-gray-500">
                                Nenhum item conferido ainda
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Right Column - Details & Photos */}
            <div className="space-y-6">
              {/* Order Details */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-6">
                  <Store className="w-5 h-5 mr-2 text-blue-600" />
                  Dados do Pedido
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">
                      Canal de Venda
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">
                        {pedido?.ID_LOJA || '  '}
                      </span>
                      <button
                        onClick={() =>
                          copy(
                            pedido?.ID_LOJA || 'MERCADO_LIVRE',
                            'ID Loja copiado!',
                          )
                        }
                        className="p-1 text-gray-400 hover:text-blue-600 rounded"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">
                      OrderID
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900 font-mono text-sm">
                        {pedido?.IDPEDIDOLOJA || '  '}
                      </span>
                      <button
                        onClick={() =>
                          copy(
                            pedido?.IDPEDIDOLOJA || 'MLB-987654321',
                            'OrderID copiado!',
                          )
                        }
                        className="p-1 text-gray-400 hover:text-blue-600 rounded"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">
                      Ship ID
                    </span>
                    <span className="font-medium text-gray-900 font-mono text-sm">
                      {pedido?.SHIP || ' '}
                    </span>
                  </div>
                </div>
              </div>

              {/* Customer Details */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-6">
                  <User className="w-5 h-5 mr-2 text-green-600" />
                  Dados do Cliente
                </h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">
                      Nome
                    </span>
                    <p className="font-medium text-gray-900 mt-1">
                      {pedido?.CLIENTE || 'João da Silva Santos'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">
                      CPF/CNPJ
                    </span>
                    <p className="font-medium text-gray-900 mt-1 font-mono">
                      {pedido?.CPF || '123.456.789-00'}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">
                      Endereço
                    </span>
                    <p className="font-medium text-gray-900 mt-1 text-sm leading-relaxed">
                      {pedido?.END_ENT || ' '}
                    </p>
                  </div>
                </div>
              </div>

              {/* Picker Details */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-6">
                  <Truck className="w-5 h-5 mr-2 text-purple-600" />
                  Dados do Conferente
                </h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">
                      Conferente
                    </span>
                    <p className="font-medium text-gray-900 mt-1">
                      {pedido?.CONFERENTE}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">
                      Data/Hora
                    </span>
                    <p className="font-medium text-gray-900 mt-1">
                      {fmtDateTime(pedido?.DATA_CONFERENCIA)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow duration-300 mt-8">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center mb-6">
              <Camera className="w-6 h-6 mr-3 text-amber-600" />
              Fotos da Separação
              {fotos.length > 0 && (
                <span className="ml-auto text-sm font-normal text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {fotos.length} {fotos.length === 1 ? 'foto' : 'fotos'}
                </span>
              )}
            </h3>

            {isLoadingFotos ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
                <p className="text-gray-500 text-sm">Carregando fotos...</p>
              </div>
            ) : fotos.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ImageIcon className="w-10 h-10 text-gray-400" />
                </div>
                <h4 className="text-lg font-medium text-gray-600 mb-2">
                  Nenhuma foto disponível
                </h4>
                <p className="text-gray-500 text-sm">
                  As fotos da separação aparecerão aqui quando disponíveis
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {fotos.map((photo, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer transform hover:scale-105 transition-all duration-200"
                    onClick={() => handleCarouselOpen(index)}
                  >
                    <div className="w-full aspect-square overflow-hidden rounded-xl border-2 border-gray-200 shadow-md hover:shadow-lg hover:border-blue-300 transition-all duration-200 bg-gradient-to-br from-gray-50 to-gray-100">
                      <img
                        src={`data:image/jpeg;base64,${photo.img}`}
                        alt={`Separação ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                        onError={(e) => {
                          e.currentTarget.src = `data:image/png;base64,${photo.img}`
                        }}
                        loading="lazy"
                      />
                    </div>

                    {/* Overlay com efeito de hover */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 rounded-xl flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-all duration-200 transform scale-75 group-hover:scale-100">
                        <div className="flex items-center bg-white/95 backdrop-blur-sm text-gray-800 px-4 py-2 rounded-full shadow-lg text-sm font-medium border border-white/20">
                          <Eye className="w-4 h-4 mr-2 text-blue-600" />
                          Visualizar
                        </div>
                      </div>
                    </div>

                    {/* Indicador de posição */}
                    <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Rodapé simples e discreto */}
            {fotos.length > 0 && !isLoadingFotos && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center">
                    <Eye className="w-3 h-3 mr-1" />
                    Clique para ampliar
                  </span>
                  <span className="bg-gray-100 px-2 py-1 rounded">
                    {fotos.length} {fotos.length === 1 ? 'item' : 'itens'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Photo Modal */}
        {showCarousel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
            <div className="relative max-w-4xl max-h-4xl w-full h-full p-4">
              <button
                onClick={handleCarouselClose}
                className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X size={20} />
              </button>

              <div className="w-full h-full flex items-center justify-center">
                <img
                  src={`data:image/jpeg;base64,${fotos[carouselIndex]?.img}`}
                  alt={`Separação ${carouselIndex + 1}`}
                  className="max-w-full max-h-full object-contain rounded-lg"
                  onError={(e) => {
                    e.currentTarget.src = `data:image/png;base64,${fotos[carouselIndex]?.img}`
                  }}
                />
              </div>

              {fotos.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setCarouselIndex((prev) =>
                        prev === 0 ? fotos.length - 1 : prev - 1,
                      )
                    }
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={() =>
                      setCarouselIndex((prev) =>
                        prev === fotos.length - 1 ? 0 : prev + 1,
                      )
                    }
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}

              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white rounded-full px-4 py-2">
                <span className="text-sm font-medium text-gray-700">
                  {carouselIndex + 1} / {fotos.length}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ZPL Modal */}
        {showModalZpl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Etiqueta de Envio
                </h3>
                <button
                  onClick={() => setShowModalZpl(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 overflow-auto max-h-96">
                <div className="bg-gray-50 rounded-xl p-8 text-center">
                  <Printer className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Etiqueta ZPL</p>
                  <p className="text-sm text-gray-500">
                    Renderização da etiqueta seria exibida aqui
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowModalZpl(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Fechar
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Imprimir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast.show && (
          <div className="fixed bottom-4 right-4 z-50">
            <div
              className={`px-6 py-4 rounded-xl shadow-lg text-white font-medium ${
                toast.variant === 'danger'
                  ? 'bg-red-600'
                  : toast.variant === 'warning'
                    ? 'bg-amber-600'
                    : 'bg-green-600'
              } animate-pulse`}
            >
              {toast.msg}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default OrderDetails
